import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const summaries = [
  {
    id: 1,
    title: 'Executive Summary — March 2026',
    generated: '06 Mar 2026, 08:00 AM',
    content: `Overall distribution performance for March 2026 shows strong momentum with total sales reaching ₱48.2M, representing a 12.4% year-over-year growth. The NCR region continues to lead with ₱18.7M in revenue, while Visayas demonstrates the highest growth rate at 19.2%.

Key highlights include exceptional performance in the Body Wash and Deodorant categories (+24% MoM), driven by summer season demand. Shampoo and Conditioner remain top revenue contributors across all territories. Mindanao territory shows recovery signals after two consecutive declining months, with new account acquisitions up 31%.

Critical attention areas: 23 accounts in Luzon have not placed orders in 45+ days, representing ₱1.2M in at-risk revenue. Inventory levels for SKU-SH01 (Shampoo 400ml) are critically low in Visayas, requiring immediate replenishment to avoid stockouts during peak demand.

AI confidence in these assessments: 91.3% based on 36-month historical dataset.`,
    tags: ['Sales', 'Territory', 'Inventory', 'Growth'],
    confidence: 91.3,
  },
  {
    id: 2,
    title: 'Q2 2026 Strategic Outlook',
    generated: '05 Mar 2026, 06:00 PM',
    content: `Q2 2026 presents significant growth opportunities driven by the Philippine fiesta season (April-June) and school opening in June. AI models project total Q2 revenue of ₱155.5M, a 15.8% increase over Q2 2025.

The Deodorant and Facial Wash categories are expected to outperform with 28% and 22% growth respectively. Shampoo and Body Wash continue to anchor the portfolio. Cebu and Iloilo markets show highest untapped potential with an estimated ₱8.2M in addressable revenue from unserved accounts.

Recommended strategic priorities: expand Visayas coverage by 15 new accounts, pre-position Shampoo and Conditioner inventory for fiesta season by April 15, and launch loyalty program for top 50 accounts to defend against competitive pressure.`,
    tags: ['Forecast', 'Strategy', 'Q2', 'Expansion'],
    confidence: 87.6,
  },
];

export default function NLPSummaryPanel() {
  const [active, setActive] = useState(0);
  const [copying, setCopying] = useState(false);

  const current = summaries?.[active];

  const handleCopy = () => {
    setCopying(true);
    setTimeout(() => setCopying(false), 1500);
  };

  return (
    <div className="card p-4 lg:p-6 table-panel-depth">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center rounded-lg" style={{ width: 40, height: 40, background: 'var(--color-secondary)18' }}>
            <Icon name="FileText" size={20} color="var(--color-secondary)" strokeWidth={2} />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-base md:text-lg text-foreground">AI Natural Language Summaries</h3>
            <p className="font-caption text-xs text-muted-foreground">Auto-generated executive briefings</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {summaries?.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`w-2 h-2 rounded-full transition-all duration-200 ${active === i ? 'bg-primary w-5' : 'bg-border hover:bg-muted-foreground'}`}
            />
          ))}
        </div>
      </div>
      <div className="bg-muted rounded-xl p-4 mb-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h4 className="font-heading font-semibold text-sm text-foreground">{current?.title}</h4>
            <div className="flex items-center gap-1.5 mt-1">
              <Icon name="Clock" size={11} color="var(--color-muted-foreground)" strokeWidth={2} />
              <span className="font-caption text-xs text-muted-foreground">Generated: {current?.generated}</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1">
            <Icon name="BrainCircuit" size={12} color="var(--color-success)" strokeWidth={2} />
            <span className="font-data text-xs font-semibold text-emerald-700">{current?.confidence}%</span>
          </div>
        </div>

        <div className="font-body text-sm text-foreground leading-relaxed whitespace-pre-line max-h-48 overflow-y-auto custom-scrollbar pr-1">
          {current?.content}
        </div>

        <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-border">
          {current?.tags?.map((tag) => (
            <span key={tag} className="px-2 py-0.5 bg-card border border-border rounded-full font-caption text-xs text-muted-foreground">
              #{tag}
            </span>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="outline" size="sm" iconName={copying ? 'Check' : 'Copy'} iconPosition="left" iconSize={14} onClick={handleCopy}>
          {copying ? 'Copied!' : 'Copy Text'}
        </Button>
        <Button variant="outline" size="sm" iconName="Share2" iconPosition="left" iconSize={14}>
          Share
        </Button>
        <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left" iconSize={14}>
          Regenerate
        </Button>
        <div className="ml-auto flex items-center gap-1.5">
          {summaries?.map((s, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`font-caption text-xs px-2.5 py-1 rounded-lg border transition-all ${active === i ? 'border-primary bg-blue-50 text-primary' : 'border-border text-muted-foreground hover:bg-muted'}`}
            >
              {i === 0 ? 'Mar 2026' : 'Q2 Outlook'}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}